import { BranchesOutlined, CheckCircleOutlined, DownOutlined, ExperimentOutlined, ExportOutlined, GithubOutlined, LogoutOutlined, RocketOutlined, SafetyCertificateOutlined, SettingOutlined, ShareAltOutlined } from '@ant-design/icons';
import { Avatar, Badge, message, Popover, Tag } from 'antd';
import React, { useState } from 'react';
import { Link, Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { QlikAppsPage } from './QlikAppsPage';
import { QlikAppDetailPage } from './QlikAppDetailPage';
import { ChangeReviewPage } from './ChangeReviewPage';
import { CdOverviewPage } from './CdOverviewPage';
import { CdDeploymentsPage } from './CdDeploymentsPage';
import { CdArtifactsPage } from './CdArtifactsPage';
import { CdAppsPage } from './CdAppsPage';
import { TestCenterPage } from './TestCenterPage';
import { TestPlanDetailPage } from './TestPlanDetailPage';
import { LineageCenterPage } from './LineageCenterPage';
import logo from '../../logo.svg';
import './CiDemo.less';
import { AccountSettingsPage } from './AccountSettingsPage';
import { useAuth, userInitials } from '../auth/AuthContext';

export const CiShell: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const auth = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [openAccountMenu, setOpenAccountMenu] = useState<'github' | 'user' | null>(null);
  const isReviewView = location.pathname.endsWith('/changes') || location.pathname.endsWith('/commit');
  const isCdView = location.pathname.startsWith('/cd');
  const isTestView = location.pathname.startsWith('/tests');
  const isLineageView = location.pathname.startsWith('/lineage');
  const githubLabel = auth.github ? `@${auth.github.login}` : 'Not connected';

  const signOut = async () => {
    setSigningOut(true);
    setOpenAccountMenu(null);
    await Promise.resolve();
    try {
      await auth.logout();
      history.replace('/login');
    } catch {
      setSigningOut(false);
      message.error('Click-CI could not sign you out. Please try again.');
    }
  };

  const githubMenu = (
    <div className="ci-header-menu ci-github-menu">
      <div className="ci-header-menu-summary">
        <Avatar className="ci-github-avatar" src={auth.github?.avatarUrl} icon={<GithubOutlined />} />
        <div className="ci-header-menu-identity">
          <strong>{auth.github ? (auth.github.name || auth.github.login) : 'GitHub not connected'}</strong>
          <span>{auth.github ? `@${auth.github.login}` : 'Connect a developer identity'}</span>
        </div>
        {auth.github ? <Tag color="green"><CheckCircleOutlined /> Connected</Tag> : <Tag>Not connected</Tag>}
      </div>
      <div className="ci-header-menu-context">
        {auth.github ? 'Profile and verified email only. Git automation uses the separate deployment key.' : 'GitHub is optional and cannot be used to sign in to Click-CI.'}
      </div>
      <div className="ci-header-menu-actions" role="menu" aria-label="GitHub connection menu">
        {auth.github && (
          <a className="ci-header-menu-action" role="menuitem" href={`https://github.com/${auth.github.login}`} target="_blank" rel="noreferrer">
            <ExportOutlined /> Open GitHub profile
          </a>
        )}
        <Link className="ci-header-menu-action" role="menuitem" to="/settings/account" onClick={() => setOpenAccountMenu(null)}>
          <SettingOutlined /> Manage GitHub connection
        </Link>
      </div>
    </div>
  );

  const userMenu = (
    <div className="ci-header-menu ci-user-menu">
      <div className="ci-header-menu-summary">
        <Avatar className="ci-avatar" src={auth.user?.pictureUrl}>{userInitials(auth.user?.displayName ?? 'Click CI')}</Avatar>
        <div className="ci-header-menu-identity">
          <strong>{auth.user?.displayName ?? 'Qlik user'}</strong>
          <span>{auth.user?.email ?? auth.user?.qlikTenantId ?? 'Click-CI account'}</span>
        </div>
      </div>
      <div className="ci-header-menu-context ci-qlik-session-context">
        <SafetyCertificateOutlined /> Signed in with Qlik Cloud
      </div>
      <div className="ci-header-menu-actions" role="menu" aria-label="Click-CI account menu">
        <Link className="ci-header-menu-action" role="menuitem" to="/settings/account" onClick={() => setOpenAccountMenu(null)}>
          <SettingOutlined /> Account settings
        </Link>
        <span className="ci-header-menu-divider" />
        <button className="ci-header-menu-action is-danger" role="menuitem" type="button" disabled={signingOut} onClick={() => void signOut()}>
          <LogoutOutlined /> {signingOut ? 'Signing out...' : 'Sign out'}
        </button>
      </div>
    </div>
  );

  return (
    <div className={`ci-shell ${isReviewView ? 'is-review-view' : ''} ${isCdView ? 'is-cd-view' : ''} ${isTestView ? 'is-test-view' : ''} ${isLineageView ? 'is-lineage-view' : ''}`}>
      <aside className="ci-sidebar">
        <div className="ci-brand">
          <img src={logo} alt="Click-CI logo" />
          <span>CLICK-<strong>CI</strong></span>
        </div>
        <Link className={`ci-nav-item ${location.pathname.startsWith('/apps') ? 'is-active' : ''}`} to="/apps">
          <BranchesOutlined />
          <span>Version Control</span>
        </Link>
        <Link className={`ci-nav-item ${location.pathname.startsWith('/cd') ? 'is-active' : ''}`} to="/cd/apps">
          <RocketOutlined />
          <span>Release Center</span>
        </Link>
        <Link className={`ci-nav-item ${isTestView ? 'is-active' : ''}`} to="/tests/apps">
          <ExperimentOutlined />
          <span>Quality Center</span>
        </Link>
        <Link className={`ci-nav-item ${isLineageView ? 'is-active' : ''}`} to="/lineage">
          <ShareAltOutlined />
          <span>Lineage Center</span>
        </Link>
      </aside>

      <div className="ci-workspace">
        <header className="ci-topbar">
          <div />
          <div className="ci-account-group">
            <Popover
              content={githubMenu}
              placement="bottomRight"
              trigger="click"
              open={openAccountMenu === 'github'}
              onOpenChange={open => setOpenAccountMenu(open ? 'github' : null)}
              overlayClassName="ci-account-popover"
              destroyTooltipOnHide
            >
              <button className={`ci-account-trigger ci-github-account ${auth.github ? 'is-connected' : ''}`} type="button" aria-label={`GitHub connection: ${githubLabel}${auth.github ? ', connected' : ''}`}>
                <GithubOutlined />
                <span className="ci-account-label">GitHub</span>
                <span className="ci-account-detail">{githubLabel}</span>
                <Badge status={auth.github ? 'success' : 'warning'} />
                <DownOutlined className="ci-account-chevron" />
              </button>
            </Popover>
            <Popover
              content={userMenu}
              placement="bottomRight"
              trigger="click"
              open={openAccountMenu === 'user'}
              onOpenChange={open => setOpenAccountMenu(open ? 'user' : null)}
              overlayClassName="ci-account-popover"
              destroyTooltipOnHide
            >
              <button className="ci-account-trigger ci-user" type="button" aria-label={`Click-CI account: ${auth.user?.displayName ?? 'Qlik user'}`}>
                <Avatar className="ci-avatar" src={auth.user?.pictureUrl}>{userInitials(auth.user?.displayName ?? 'Click CI')}</Avatar>
                <span className="ci-account-label">{auth.user?.displayName ?? 'Qlik user'}</span>
                <DownOutlined className="ci-account-chevron" />
              </button>
            </Popover>
          </div>
        </header>
        <main className="ci-main">
          <Switch>
            <Route exact path="/apps" component={QlikAppsPage} />
            <Route exact path="/apps/:appId/commit" component={ChangeReviewPage} />
            <Route exact path="/apps/:appId/changes" component={ChangeReviewPage} />
            <Route exact path="/apps/:appId" component={QlikAppDetailPage} />
            <Route exact path="/cd/apps" component={CdAppsPage} />
            <Route exact path="/cd/apps/:appId/deployments" component={CdDeploymentsPage} />
            <Route exact path="/cd/apps/:appId/artifacts" component={CdArtifactsPage} />
            <Route exact path="/cd/apps/:appId" component={CdOverviewPage} />
            <Route exact path="/tests/apps" component={TestCenterPage} />
            <Route exact path="/tests/plans/:planId" component={TestPlanDetailPage} />
            <Route exact path="/tests/plans/:planId/test-plan" component={TestPlanDetailPage} />
            <Route exact path="/tests/plans/:planId/execution" component={TestPlanDetailPage} />
            <Route exact path="/tests/plans/:planId/reports" component={TestPlanDetailPage} />
            <Route exact path="/lineage" component={LineageCenterPage} />
            <Route exact path="/settings/account" component={AccountSettingsPage} />
            <Redirect to="/apps" />
          </Switch>
        </main>
      </div>
    </div>
  );
};
